// src/services/documentAnalyzerService.js

import http from "./Http";

// ============================================================
// CONFIGURACIÓN
// ============================================================

const EXTENSIONES_PERMITIDAS = [
  "doc",
  "docx",
  "pdf",
];

const MAX_DOCUMENT_BYTES =
  50 * 1024 * 1024;

// ============================================================
// UTILIDADES
// ============================================================

const obtenerExtensionDesdeNombre = (
  nombreArchivo
) => {
  if (
    !nombreArchivo ||
    !String(nombreArchivo).includes(".")
  ) {
    return "";
  }

  return String(nombreArchivo)
    .split(".")
    .pop()
    .trim()
    .toLowerCase();
};

const obtenerExtension = (archivo) => {
  return obtenerExtensionDesdeNombre(
    archivo?.name
  );
};

/*
 * Extrae el mensaje de error real enviado por el backend.
 *
 * Admite:
 *
 * detail: "mensaje"
 *
 * detail: {
 *   mensaje: "...",
 *   error: "...",
 *   tipo: "..."
 * }
 *
 * detail: [
 *   {
 *     msg: "..."
 *   }
 * ]
 */
const obtenerMensajeError = (error) => {
  const respuestaBackend =
    error?.response?.data;

  const detalleBackend =
    respuestaBackend?.detail;

  // ============================================================
  // DETAIL COMO TEXTO
  // ============================================================

  if (
    typeof detalleBackend === "string" &&
    detalleBackend.trim()
  ) {
    return detalleBackend.trim();
  }

  // ============================================================
  // DETAIL COMO OBJETO
  // ============================================================

  if (
    detalleBackend &&
    typeof detalleBackend === "object" &&
    !Array.isArray(detalleBackend)
  ) {
    const mensaje =
      detalleBackend.mensaje ??
      detalleBackend.message ??
      detalleBackend.error ??
      detalleBackend.detail ??
      null;

    if (
      typeof mensaje === "string" &&
      mensaje.trim()
    ) {
      return mensaje.trim();
    }
  }

  // ============================================================
  // DETAIL COMO LISTA DE VALIDACIONES
  // ============================================================

  if (Array.isArray(detalleBackend)) {
    const mensajes = detalleBackend
      .map((item) => {
        if (
          typeof item === "string"
        ) {
          return item.trim();
        }

        return (
          item?.msg ??
          item?.mensaje ??
          item?.message ??
          item?.error ??
          null
        );
      })
      .filter(
        (item) =>
          typeof item === "string" &&
          item.trim()
      );

    if (mensajes.length > 0) {
      return mensajes.join(". ");
    }
  }

  // ============================================================
  // OTROS CAMPOS HABITUALES DEL BACKEND
  // ============================================================

  const mensajeBackend =
    respuestaBackend?.mensaje ??
    respuestaBackend?.message ??
    respuestaBackend?.error ??
    null;

  if (
    typeof mensajeBackend === "string" &&
    mensajeBackend.trim()
  ) {
    return mensajeBackend.trim();
  }

  // ============================================================
  // ERROR DE AXIOS O DEL NAVEGADOR
  // ============================================================

  if (
    error?.code === "ECONNABORTED"
  ) {
    return (
      "El análisis tardó demasiado. " +
      "Intente nuevamente."
    );
  }

  if (
    error?.code === "ERR_NETWORK"
  ) {
    return (
      "No se pudo conectar con el servidor " +
      "de análisis."
    );
  }

  if (
    typeof error?.message === "string" &&
    error.message.trim()
  ) {
    return error.message.trim();
  }

  return "No se pudo analizar el documento.";
};

// ============================================================
// VALIDAR DOCUMENTO
// ============================================================

const validarDocumento = (archivo) => {
  if (!archivo) {
    return {
      valido: false,
      mensaje:
        "Debe seleccionar un archivo.",
    };
  }

  const esArchivoReal =
    typeof File !== "undefined" &&
    archivo instanceof File;

  if (!esArchivoReal) {
    return {
      valido: false,
      mensaje:
        "El archivo seleccionado no es válido.",
    };
  }

  const extension =
    obtenerExtension(archivo);

  if (
    !EXTENSIONES_PERMITIDAS.includes(
      extension
    )
  ) {
    return {
      valido: false,
      mensaje:
        "Formato no permitido. " +
        "Solo se admiten archivos DOC, DOCX o PDF.",
    };
  }

  if (!archivo.size) {
    return {
      valido: false,
      mensaje:
        "El archivo seleccionado está vacío.",
    };
  }

  if (
    archivo.size >
    MAX_DOCUMENT_BYTES
  ) {
    return {
      valido: false,
      mensaje:
        "El archivo supera el tamaño máximo " +
        "permitido de 50 MB.",
    };
  }

  /*
   * No se valida aquí la cantidad de páginas.
   *
   * El navegador no puede determinar de forma fiable
   * la cantidad de páginas de DOC, DOCX y PDF sin
   * procesar previamente el documento.
   *
   * La validación de páginas corresponde al backend.
   */
  return {
    valido: true,
    mensaje: null,
  };
};

// ============================================================
// NORMALIZAR RESPUESTA
// ============================================================

const normalizarResultadoDocumento = (
  data
) => {
  if (!data || typeof data !== "object") {
    throw new Error(
      "El backend devolvió una respuesta " +
        "de análisis inválida."
    );
  }

  const areaBackend =
    data.area_tematica ??
    data.area ??
    null;

  const nivel = data.nivel
    ? {
        id:
          data.nivel.id ?? null,

        nombre:
          data.nivel.nombre ?? "",
      }
    : null;

  const area = areaBackend
    ? {
        id:
          areaBackend.id ?? null,

        nombre:
          areaBackend.nombre ?? "",
      }
    : null;

  const competencia = data.competencia
    ? {
        id:
          data.competencia.id ?? null,

        nombre:
          data.competencia.nombre ?? "",

        justificacion:
          data.competencia.justificacion ??
          null,
      }
    : null;

  const grado = data.grado
    ? {
        id:
          data.grado.id ?? null,

        numero:
          data.grado.numero ?? null,

        nombre:
          data.grado.nombre ?? "",

        descripcion:
          data.grado.nombre ??
          data.grado.descripcion ??
          null,
      }
    : null;

  const tituloSesion =
    typeof data.titulo_sesion === "string"
      ? data.titulo_sesion.trim() || null
      : null;

  const propositoSesion =
    typeof data.proposito_sesion === "string"
      ? data.proposito_sesion.trim() || null
      : null;

  const fileId =
    typeof data.file_id === "string"
      ? data.file_id.trim() || null
      : null;

  const cantidadPaginasNumero =
    Number(data.cantidad_paginas);

  const cantidadPaginas =
    Number.isInteger(
      cantidadPaginasNumero
    ) &&
    cantidadPaginasNumero > 0
      ? cantidadPaginasNumero
      : null;

  const encontrado =
    typeof data.encontrado === "boolean"
      ? data.encontrado
      : Boolean(
          nivel ||
            area ||
            grado ||
            competencia ||
            tituloSesion ||
            propositoSesion
        );

  return {
    encontrado,

    archivo:
      typeof data.archivo === "string"
        ? data.archivo.trim() || null
        : null,

    nivel,
    area,
    competencia,
    grado,

    titulo_sesion:
      tituloSesion,

    proposito_sesion:
      propositoSesion,

    file_id:
      fileId,

    cantidad_paginas:
      cantidadPaginas,

    cantidad_sesiones:
      Number(data.cantidad_sesiones) || 1,

    mensaje:
      data.mensaje ??
      (
        encontrado
          ? null
          : (
              "No se encontraron datos " +
              "curriculares suficientes."
            )
      ),
  };
};

// ============================================================
// LIMPIAR CONTENT-TYPE
// ============================================================

const eliminarContentType = (headers) => {
  if (!headers) {
    return;
  }

  if (
    typeof headers.delete === "function"
  ) {
    headers.delete("Content-Type");
    headers.delete("content-type");

    return;
  }

  delete headers["Content-Type"];
  delete headers["content-type"];

  if (headers.common) {
    delete headers.common["Content-Type"];
    delete headers.common["content-type"];
  }

  if (headers.post) {
    delete headers.post["Content-Type"];
    delete headers.post["content-type"];
  }
};

// ============================================================
// ANALIZAR DOCUMENTO
// ============================================================

const analizarDocumento = async (
  archivo
) => {
  const validacion =
    validarDocumento(archivo);

  if (!validacion.valido) {
    const error = new Error(
      validacion.mensaje
    );

    error.esValidacionDocumento = true;

    throw error;
  }

  const formData = new FormData();

  formData.append(
    "file",
    archivo,
    archivo.name
  );

  const archivoAdjunto =
    formData.get("file");

  const esArchivoAdjuntoValido =
    typeof File !== "undefined" &&
    archivoAdjunto instanceof File;

  if (!esArchivoAdjuntoValido) {
    throw new Error(
      "No se pudo adjuntar correctamente " +
        "el archivo a la solicitud."
    );
  }

  try {
    const response = await http.request({
      method: "POST",

      url:
        "/analizador-documentos/analizar",

      data:
        formData,

      /*
       * No se establece manualmente multipart/form-data.
       *
       * El navegador debe generar automáticamente el
       * boundary correspondiente al FormData.
       */
      headers: {
        "Content-Type": undefined,
      },

      transformRequest: [
        (data, headers) => {
          eliminarContentType(headers);

          return data;
        },
      ],
    });

    return normalizarResultadoDocumento(
      response.data
    );
  } catch (error) {
    const mensaje =
      obtenerMensajeError(error);

    const status =
      error?.response?.status ??
      null;

    const detalle =
      error?.response?.data ??
      null;

    console.error(
      "Error en analizarDocumento:",
      {
        mensaje,
        status,
        detalle,
        error,
      }
    );

    /*
     * Este error conserva la información técnica para
     * poder inspeccionarla desde FileUploadArea o desde
     * la consola del navegador.
     */
    const errorNormalizado =
      new Error(mensaje);

    errorNormalizado.name =
      "DocumentAnalyzerError";

    errorNormalizado.status =
      status;

    errorNormalizado.detalle =
      detalle;

    errorNormalizado.causaOriginal =
      error;

    throw errorNormalizado;
  }
};

// ============================================================
// INFORMACIÓN DEL ARCHIVO REAL
// ============================================================

const obtenerInfoArchivo = (
  archivo
) => {
  if (!archivo) {
    return null;
  }

  const tamañoBytes =
    Number(archivo.size) || 0;

  const tamañoKB =
    tamañoBytes / 1024;

  const tamañoMB =
    tamañoKB / 1024;

  return {
    nombre:
      archivo.name || "documento",

    tamañoBytes,

    tamaño:
      tamañoMB >= 1
        ? `${tamañoMB.toFixed(2)} MB`
        : `${tamañoKB.toFixed(2)} KB`,

    extension:
      obtenerExtension(archivo),

    tipoMime:
      archivo.type || null,

    cantidadPaginas:
      null,
  };
};

// ============================================================
// INFORMACIÓN DEL ARCHIVO PERSISTIDO
// ============================================================

const obtenerInfoArchivoPersistido = ({
  archivo,
  cantidadPaginas,
  infoOriginal,
}) => {
  const nombreNormalizado =
    typeof archivo === "string"
      ? archivo.trim()
      : "";

  const nombre =
    nombreNormalizado ||
    infoOriginal?.nombre ||
    "documento.pdf";

  const paginasNumero =
    Number(cantidadPaginas);

  const paginasValidas =
    Number.isInteger(paginasNumero) &&
    paginasNumero > 0
      ? paginasNumero
      : (
          infoOriginal?.cantidadPaginas ??
          null
        );

  return {
    nombre,

    tamañoBytes:
      infoOriginal?.tamañoBytes ??
      null,

    tamaño:
      infoOriginal?.tamaño ??
      null,

    extension:
      obtenerExtensionDesdeNombre(
        nombre
      ) || "pdf",

    tipoMime:
      "application/pdf",

    cantidadPaginas:
      paginasValidas,
  };
};

// ============================================================
// EXPORTACIÓN
// ============================================================

const documentAnalyzerService = {
  analizarDocumento,
  validarDocumento,
  obtenerInfoArchivo,
  obtenerInfoArchivoPersistido,
  obtenerMensajeError,
};

export default documentAnalyzerService;